/*
 * product-addons.js
 * Click-to-select rail cards, "see more" popup, hidden-input syncing,
 * and bundled /cart/add.js submission for the bouquet + selected addons.
 * Loaded conditionally on product templates via layout/theme.liquid.
 */
(function () {
  'use strict';

  function init() {
    bindCardClicks();
    bindRailTabs();
    bindPopup();
    bindFormSubmit();
  }

  function bindCardClicks() {
    document.querySelectorAll('[data-addon-card]').forEach(function (card) {
      card.addEventListener('click', function (event) {
        // Don't intercept clicks on the "see more" link; that's handled separately.
        if (event.target.closest('[data-addon-see-more]')) return;
        if (card.getAttribute('aria-disabled') === 'true') return;
        toggleCardSelection(card);
      });
      card.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.target.closest('[data-addon-see-more]')) return;
        if (card.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        toggleCardSelection(card);
      });
    });
  }

  function toggleCardSelection(card) {
    var rail = card.closest('[data-addon-rail]');
    if (!rail) return;
    var mode = rail.dataset.selectionMode;
    var isSelected = card.getAttribute('aria-pressed') === 'true';

    if (mode === 'single') {
      // Deselect any other selected card in this rail first.
      rail.querySelectorAll('[data-addon-card][aria-pressed="true"]').forEach(function (other) {
        if (other !== card) other.setAttribute('aria-pressed', 'false');
      });
      card.setAttribute('aria-pressed', isSelected ? 'false' : 'true');
    } else {
      // multi: toggle independently
      card.setAttribute('aria-pressed', isSelected ? 'false' : 'true');
    }

    syncRailHiddenInputs(rail);
    updateTabCounts(rail);
  }

  var SINGLE_RAIL_SELECTORS = {
    vase: '[data-addon-vase-id]',
    card: '[data-addon-card-id]',
    pot: '[data-addon-pot-id]'
  };
  var MULTI_RAIL_INPUT_NAMES = {
    giftware: 'addon_giftware_ids[]',
    'plant-addon': 'addon_plant_addon_ids[]'
  };

  function syncRailHiddenInputs(rail) {
    var railId = rail.dataset.railId;
    if (SINGLE_RAIL_SELECTORS[railId]) {
      var input = rail.querySelector(SINGLE_RAIL_SELECTORS[railId]);
      if (!input) return;
      var selected = rail.querySelector('[data-addon-card][aria-pressed="true"]');
      input.value = selected ? selected.dataset.variantId : '';
    } else if (MULTI_RAIL_INPUT_NAMES[railId]) {
      // Variable number of hidden inputs. Remove existing ones and recreate from selection.
      var name = MULTI_RAIL_INPUT_NAMES[railId];
      rail.querySelectorAll('input[name="' + name + '"]').forEach(function (i) {
        i.parentNode.removeChild(i);
      });
      rail.querySelectorAll('[data-addon-card][aria-pressed="true"]').forEach(function (card) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = name;
        hidden.value = card.dataset.variantId;
        rail.appendChild(hidden);
      });
    }
  }

  /* ---------- Category tabs ----------
   * Tabs filter the rail's cards in place; they never touch selection state.
   * Selection lives on each card's aria-pressed and the hidden inputs hang off
   * the rail (not the scroller), so hiding a card leaves it in the cart bundle.
   * That's the point of the per-tab counts: a selection made in one tab is
   * otherwise invisible from another.
   */
  function bindRailTabs() {
    document.querySelectorAll('[data-addon-rail-tabs]').forEach(function (tablist) {
      var rail = tablist.closest('[data-addon-rail]');
      if (!rail) return;
      var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[data-addon-rail-tab]'));
      if (tabs.length < 2) return;

      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () {
          activateTab(rail, tabs, tab);
        });
        tab.addEventListener('keydown', function (event) {
          var target = null;
          if (event.key === 'ArrowRight') {
            target = tabs[(index + 1) % tabs.length];
          } else if (event.key === 'ArrowLeft') {
            target = tabs[(index - 1 + tabs.length) % tabs.length];
          } else if (event.key === 'Home') {
            target = tabs[0];
          } else if (event.key === 'End') {
            target = tabs[tabs.length - 1];
          }
          if (!target) return;
          event.preventDefault();
          activateTab(rail, tabs, target);
          target.focus();
        });
      });

      updateTabCounts(rail);
      bindTabArrows(tablist);
      bindTabDrag(tablist);
    });
  }

  /* True while the strip has somewhere to scroll. The 1px tolerance keeps
   * sub-pixel pill widths from reading as overflow on a strip that fits. */
  function tabsOverflow(tablist) {
    return tablist.scrollWidth - tablist.clientWidth > 1;
  }

  /* Desktop scroll arrows for the tab strip.
   * A mouse user gets no scrollbar and no shift+wheel affordance, so the
   * arrows are the visible cue that more tabs exist — each one shows only
   * while there is somewhere to scroll that way, which makes it the overflow
   * indicator as well as the control. They complement the click-and-hold drag
   * below rather than replacing it. Touch is unaffected — CSS hides these
   * where dragging is native.
   */
  function bindTabArrows(tablist) {
    var wrap = tablist.closest('[data-addon-rail-tabs-wrap]');
    if (!wrap) return;
    var prev = wrap.querySelector('[data-addon-tabs-arrow="prev"]');
    var next = wrap.querySelector('[data-addon-tabs-arrow="next"]');
    if (!prev || !next) return;

    function refresh() {
      // Sub-pixel widths make an exact comparison flicker the arrow at the
      // travel limits, so both ends get a 1px tolerance.
      var max = tablist.scrollWidth - tablist.clientWidth;
      prev.hidden = tablist.scrollLeft <= 1;
      next.hidden = tablist.scrollLeft >= max - 1;
      // Drives the grab cursor: no overflow, nothing to drag, normal pointer.
      tablist.classList.toggle('is-scrollable', tabsOverflow(tablist));
    }

    function step(direction) {
      var by = Math.max(tablist.clientWidth * 0.8, 80) * direction;
      if (tablist.scrollBy) {
        tablist.scrollBy({ left: by });
      } else {
        tablist.scrollLeft += by;
      }
    }

    prev.addEventListener('click', function () { step(-1); });
    next.addEventListener('click', function () { step(1); });
    tablist.addEventListener('scroll', refresh);
    window.addEventListener('resize', refresh);
    // Web fonts land after DOMContentLoaded and change the pill widths, which
    // changes whether the strip overflows at all.
    window.addEventListener('load', refresh);
    refresh();
  }

  /* Click-and-hold drag to pan the tab strip.
   * Mouse only: touch and pen already pan natively, and capturing their
   * pointers would fight that. A movement past DRAG_SLOP marks the gesture as
   * a drag, which swallows the click it ends on — otherwise letting go over a
   * tab would switch category as a side effect of scrolling.
   */
  var DRAG_SLOP = 4;

  function bindTabDrag(tablist) {
    var pointerId = null;
    var startX = 0;
    var startScroll = 0;
    var dragged = false;
    var swallowClick = false;

    tablist.addEventListener('pointerdown', function (event) {
      // Every gesture starts clean, so a drag that never produced a click
      // can't leave the suppression armed for the next one.
      swallowClick = false;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!tabsOverflow(tablist)) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = tablist.scrollLeft;
      dragged = false;
    });

    tablist.addEventListener('pointermove', function (event) {
      if (pointerId === null || event.pointerId !== pointerId) return;
      var dx = event.clientX - startX;
      if (!dragged) {
        if (Math.abs(dx) < DRAG_SLOP) return;
        dragged = true;
        // Capture so the drag survives the cursor leaving the strip, and drop
        // the smooth scroll-behavior (via .is-dragging) — during a drag the
        // pointer IS the animation, and easing each scrollLeft assignment
        // lags the strip behind the hand.
        if (tablist.setPointerCapture) tablist.setPointerCapture(pointerId);
        tablist.classList.add('is-dragging');
      }
      // Stops the browser starting a text/element drag mid-pan.
      event.preventDefault();
      tablist.scrollLeft = startScroll - dx;
    });

    function endDrag(event) {
      if (pointerId === null || event.pointerId !== pointerId) return;
      if (tablist.hasPointerCapture && tablist.hasPointerCapture(pointerId)) {
        tablist.releasePointerCapture(pointerId);
      }
      pointerId = null;
      tablist.classList.remove('is-dragging');
      swallowClick = dragged;
    }

    tablist.addEventListener('pointerup', endDrag);
    tablist.addEventListener('pointercancel', endDrag);

    // Capture phase, so it beats the per-tab click listener.
    tablist.addEventListener('click', function (event) {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  }

  function activateTab(rail, tabs, active) {
    tabs.forEach(function (tab) {
      var isActive = tab === active;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('is-active', isActive);
    });

    filterRailCards(rail, active.dataset.tabValue || '');

    // Without this the new tab opens part-scrolled, wherever the last one sat.
    var scroller = rail.querySelector('.addon-rail__scroller');
    if (scroller) scroller.scrollLeft = 0;
  }

  function cardInTab(card, value) {
    if (!value) return true;  // the "All" tab
    var values = card.dataset.tabValues || '';
    return values.indexOf('|' + value + '|') !== -1;
  }

  function filterRailCards(rail, value) {
    rail.querySelectorAll('[data-addon-card]').forEach(function (card) {
      // display:none rather than visibility:hidden so filtered-out cards leave
      // the tab order too — a hidden sold-out card must not stay focusable.
      card.classList.toggle('addon-card--filtered-out', !cardInTab(card, value));
    });
  }

  function updateTabCounts(rail) {
    var tabs = rail.querySelectorAll('[data-addon-rail-tab]');
    if (!tabs.length) return;
    var selected = Array.prototype.slice.call(
      rail.querySelectorAll('[data-addon-card][aria-pressed="true"]')
    );

    tabs.forEach(function (tab) {
      var value = tab.dataset.tabValue || '';
      var count = selected.filter(function (card) {
        return cardInTab(card, value);
      }).length;
      var badge = tab.querySelector('[data-addon-tab-count]');
      if (!badge) return;
      badge.textContent = count ? String(count) : '';
      badge.hidden = count === 0;
      tab.classList.toggle('has-selection', count > 0);
    });
  }

  function bindPopup() {
    var popup = document.querySelector('[data-addon-popup]');
    if (!popup) return;

    var imageEl = popup.querySelector('[data-addon-popup-image]');
    var titleEl = popup.querySelector('[data-addon-popup-title]');
    var descEl = popup.querySelector('[data-addon-popup-description]');
    var lastTrigger = null;

    document.querySelectorAll('[data-addon-see-more]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var card = link.closest('[data-addon-card]');
        if (!card) return;
        lastTrigger = link;
        openPopup({
          image: card.dataset.productImage,
          title: card.dataset.productTitle,
          description: card.dataset.productDescription
        });
      });
    });

    popup.querySelector('[data-addon-popup-close]').addEventListener('click', closePopup);
    popup.querySelector('[data-addon-popup-backdrop]').addEventListener('click', closePopup);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && popup.getAttribute('aria-hidden') === 'false') {
        closePopup();
      }
    });

    function openPopup(data) {
      imageEl.src = data.image || '';
      imageEl.alt = data.title || '';
      titleEl.textContent = data.title || '';
      // Description is stored as escaped plain text in the data attribute.
      descEl.textContent = data.description || '';
      popup.hidden = false;
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var panel = popup.querySelector('.addon-popup__panel');
      panel.focus();
    }

    function closePopup() {
      popup.hidden = true;
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastTrigger) {
        lastTrigger.focus();
        lastTrigger = null;
      }
    }
  }

  function bindFormSubmit() {
    var rails = document.querySelectorAll('[data-addon-rail]');
    if (rails.length === 0) return;

    var firstRail = rails[0];
    var form = firstRail.closest('form[action*="/cart/add"]');
    if (!form) return;

    // The theme attaches an AJAX handler directly to the add-to-cart button's
    // click event (and calls preventDefault on the click) — so the form's
    // 'submit' event never fires. Intercept the click in capture phase so we
    // run before the theme's handler, and stopImmediatePropagation to prevent
    // the theme's add-to-cart from running when we need to bundle addons.
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var btn = target.closest('#purchase, button[name="add"], input[type="submit"][name="add"]');
      if (!btn || !form.contains(btn)) return;

      var addons = collectAddonItems(form);
      if (addons.length === 0) return; // No addons selected — let theme handle normally.

      if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      submitBundle(form, addons);
    }, true);

    // Also handle the native form submit path as a fallback (e.g., if a
    // future theme update stops intercepting the click).
    form.addEventListener('submit', function (event) {
      var addons = collectAddonItems(form);
      if (addons.length === 0) return;

      if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      submitBundle(form, addons);
    });
  }

  function collectAddonItems(form) {
    var items = [];
    var bouquetHandle = (form.querySelector('[name="addon_for_handle"]') || {}).value
      || (window.wetheme && window.wetheme.product && window.wetheme.product.handle)
      || '';
    // Fallback: read from the URL path (e.g. /products/lily-bouquet)
    if (!bouquetHandle) {
      var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
      bouquetHandle = match ? match[1] : '';
    }

    // Single-select rails (vase, card, pot)
    Object.keys(SINGLE_RAIL_SELECTORS).forEach(function (railId) {
      var hidden = form.querySelector(SINGLE_RAIL_SELECTORS[railId]);
      if (hidden && hidden.value) {
        items.push({
          id: parseInt(hidden.value, 10),
          quantity: 1,
          properties: { '_addon_for': bouquetHandle }
        });
      }
    });

    // Multi-select rails (giftware, plant-addon)
    Object.keys(MULTI_RAIL_INPUT_NAMES).forEach(function (railId) {
      var name = MULTI_RAIL_INPUT_NAMES[railId];
      form.querySelectorAll('input[name="' + name + '"]').forEach(function (input) {
        if (input.value) {
          items.push({
            id: parseInt(input.value, 10),
            quantity: 1,
            properties: { '_addon_for': bouquetHandle }
          });
        }
      });
    });

    return items;
  }

  function submitBundle(form, addonItems) {
    var formData = new FormData(form);

    // Bouquet variant id is in <select name="id"> or <input name="id">.
    var bouquetId = formData.get('id');
    var bouquetQty = parseInt(formData.get('quantity'), 10) || 1;

    // Harvest all properties[*] inputs from the form.
    var bouquetProperties = {};
    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match && value) {
        bouquetProperties[match[1]] = value;
      }
    });

    var payload = {
      items: [{
        id: parseInt(bouquetId, 10),
        quantity: bouquetQty,
        properties: bouquetProperties
      }].concat(addonItems)
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (body) {
          throw new Error(body.description || body.message || 'Add to cart failed.');
        });
      }
      // Hand off: redirect to /cart matches the simplest behavior expected by
      // the theme. If the theme has a cart drawer or AJAX cart, the existing
      // wetheme code will pick it up after the redirect (or replace this with
      // the theme's own update routine if discovered during integration).
      window.location.href = '/cart';
    }).catch(function (err) {
      showAddError(form, err.message);
    });
  }

  function showAddError(form, message) {
    var existing = form.querySelector('[data-addon-error]');
    if (existing) existing.parentNode.removeChild(existing);
    var div = document.createElement('div');
    div.className = 'addon-error';
    div.setAttribute('data-addon-error', '');
    div.setAttribute('role', 'alert');
    div.textContent = message || 'Sorry, something went wrong adding your items. Please try again.';
    div.style.color = '#b00';
    div.style.margin = '8px 0';
    // Insert right before the submit button if possible, else at the end of the form.
    var submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submitBtn && submitBtn.parentNode) {
      submitBtn.parentNode.insertBefore(div, submitBtn);
    } else {
      form.appendChild(div);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
